import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";

// Interview orb — "aurora + pulse".
//
// A conic-gradient ring (navy → sky → aqua → teal) spins around a white inset and
// a navy core. The whole thing is presentational and animates in a local rAF /
// via CSS keyframes — it never triggers a React re-render per frame.
//
//   speaking  → FAST spin + expanding pulse rings; the core breathes with the live
//               TTS amplitude (levelRef).
//   listening → SLOW spin, calm TEAL gradient, no pulse — "your turn".
//   thinking  → slow spin + gentle idle breathe while we wait on the network.
//   idle      → static.
const NAVY = "#00438e";
const SKY = "#2ea3ff";
const AQUA = "#39d0c6";
const TEAL = "#0e9c94";

// The aurora's colours; a survey can tint the orb with its own (`palette`).
export const DEFAULT_ORB_PALETTE = { navy: NAVY, sky: SKY, aqua: AQUA, teal: TEAL, light: "#7fe3da" };

export default function InterviewOrb({ mode = "idle", levelRef, insetColor = "#f6f8fc", glow = "none", palette }) {
    const { navy: NAVY_, sky: SKY_, aqua: AQUA_, teal: TEAL_, light: LIGHT_ } = { ...DEFAULT_ORB_PALETTE, ...palette };
    const ringRef = useRef(null); // conic ring wrapper — bloom-scaled by level while speaking
    const coreRef = useRef(null); // navy/teal core — breathes with the level while speaking

    const isSpeaking = mode === "speaking";
    const isListening = mode === "listening";
    const isThinking = mode === "thinking";

    // Drive the live "breathing" from the TTS amplitude, but only while speaking.
    useEffect(() => {
        if (mode !== "speaking") {
            if (ringRef.current) ringRef.current.style.transform = "scale(1)";
            if (coreRef.current) coreRef.current.style.transform = "scale(1)";
            return undefined;
        }
        let raf;
        const tick = () => {
            const lvl = levelRef && typeof levelRef.current === "number" ? levelRef.current : 0;
            const ringScale = 1 + 0.03 + lvl * 0.14; // subtle bloom of the whole ring
            const coreScale = 1 + 0.06 + lvl * 0.34; // the core "speaks"
            if (ringRef.current) ringRef.current.style.transform = `scale(${ringScale.toFixed(3)})`;
            if (coreRef.current) coreRef.current.style.transform = `scale(${coreScale.toFixed(3)})`;
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [mode, levelRef]);

    // Teal-only gradient on the respondent's turn; full aurora otherwise.
    const conic = isListening
        ? `conic-gradient(from 0deg, ${TEAL_}, ${AQUA_}, ${LIGHT_}, ${AQUA_}, ${TEAL_})`
        : `conic-gradient(from 0deg, ${NAVY_}, ${SKY_}, ${AQUA_}, ${TEAL_}, ${SKY_}, ${NAVY_})`;

    // Spin fast while speaking, slow otherwise. Only actually animate off-idle.
    const spinDuration = isSpeaking ? "3.4s" : isListening ? "11s" : "16s";
    const spinning = isSpeaking || isListening || isThinking;
    const coreColor = isListening ? TEAL_ : NAVY_;
    const coreHi = isListening ? AQUA_ : SKY_;

    return (
        <Box
            sx={{
                position: "relative",
                width: { xs: 188, sm: 220 },
                height: { xs: 188, sm: 220 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "@keyframes orb-spin": { to: { transform: "rotate(360deg)" } },
                "@keyframes orb-pulse-ring": {
                    "0%": { transform: "scale(0.6)", opacity: 0.5 },
                    "100%": { transform: "scale(1.18)", opacity: 0 },
                },
                "@keyframes orb-breathe": {
                    "0%, 100%": { transform: "scale(0.98)" },
                    "50%": { transform: "scale(1.03)" },
                },
            }}
        >
            {/* Expanding pulse rings — only while the AI is speaking */}
            {isSpeaking &&
                [0, 1].map((i) => (
                    <Box
                        key={i}
                        aria-hidden="true"
                        sx={{
                            position: "absolute",
                            width: "91%",
                            height: "91%",
                            borderRadius: "50%",
                            border: `2px solid ${AQUA_}`,
                            animation: `orb-pulse-ring 1.9s ease-out ${i * 0.95}s infinite`,
                        }}
                    />
                ))}

            {/* Ring wrapper — bloom-scaled by the level while speaking */}
            <Box
                ref={ringRef}
                aria-hidden="true"
                sx={{
                    position: "relative",
                    width: "91%",
                    height: "91%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    willChange: "transform",
                    transition: isSpeaking ? "none" : "transform 0.4s ease",
                }}
            >
                {/* Spinning conic aurora */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        background: conic,
                        filter: "blur(1.5px)",
                        opacity: 0.95,
                        animation: spinning ? `orb-spin ${spinDuration} linear infinite` : "none",
                    }}
                />
                {/* Inset — carves the aurora into a ring (themed: light by day, deep
                    navy with a soft outer glow by night) */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        width: "76%",
                        height: "76%",
                        borderRadius: "50%",
                        backgroundColor: insetColor,
                        boxShadow: glow && glow !== "none" ? `inset 0 0 22px rgba(0,0,0,0.25), ${glow}` : "inset 0 0 22px rgba(0,0,0,0.06)",
                    }}
                />
                {/* Core */}
                <Box
                    ref={coreRef}
                    aria-hidden="true"
                    sx={{
                        position: "relative",
                        width: "55%",
                        height: "55%",
                        borderRadius: "50%",
                        background: `radial-gradient(circle at 38% 32%, ${coreHi} 0%, ${coreColor} 62%, ${NAVY_} 100%)`,
                        boxShadow: `0 10px 30px ${isListening ? "rgba(14,156,148,0.4)" : "rgba(0,67,142,0.4)"}`,
                        animation: !isSpeaking && (isListening || isThinking) ? "orb-breathe 3.4s ease-in-out infinite" : "none",
                        transition: isSpeaking ? "none" : "transform 0.4s ease",
                        willChange: "transform",
                    }}
                />
            </Box>
        </Box>
    );
}
